import { Text } from "@ucl/react-library";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BankDetailsForm } from "src/components/bank-details/bank-details-form";
import { mapCustomerUpdateFormData } from "src/utils/map-customer-update-data";
import { updateCustomerDetails } from "src/api/services/customer-service";
import { CaseFormTypes } from "../case-landing/case-form-setup";
import { ContactDetailsSection } from "src/components/contact-details/contact-details-section";
import { useMultiStepForm } from "src/hooks/use-multi-step-form";
import { MultiStepFormLayout } from "src/components/multi-step-form-layout/multi-step-form-layout";
import { useNavigate } from "react-router-dom";
import { useCase } from "src/context/case-context";
import { Loader } from "src/utils/loader";

type StepConfig = {
  step: number;
  body: ReactNode;
};

export const BankDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerData, setCustomerData } = useCase();
  const [showLoadingPage, setShowLoadingPage] = useState(false);

  const {
    showEditDetails,
    setShowEditDetails,
    showError,
    setShowError,
    methods,
    isAgreed,
    currentStep,
    totalSteps,
    handleContinue,
    handleBack,
  } = useMultiStepForm({
    initialSections: ["bankDetails", "agreeTsAndCs"],
  });

  const steps = useMemo<StepConfig[]>(
    () => [
      {
        step: 1,
        body: (
          <div data-testid="bankContactDetails" className="flex flex-col gap-10">
            <div className="flex flex-col gap-4">
              <Text renderAs="h2" styleType="h4" fontWeight="semi-bold">
                {t("bankDetailsForm.step1Title")}
              </Text>
              <Text.P size="large">{t("bankDetailsForm.step1Subtitle")}</Text.P>
            </div>
            <ContactDetailsSection
              showContactPreference={true}
              showContactExplainer={true}
              showEditDetails={showEditDetails}
              toggleContactDetails={() => setShowEditDetails(!showEditDetails)}
            />
          </div>
        ),
      },
      {
        step: 2,
        body: <BankDetailsForm />,
      },
    ],
    [showEditDetails, t]
  );

  if (!customerData) return null;

  const onSubmit = async (values: CaseFormTypes) => {
    setShowError(false);
    setShowLoadingPage(true);

    try {
      const mappedData = mapCustomerUpdateFormData(values);

      const updatedData = await updateCustomerDetails(mappedData);
      setCustomerData(updatedData);

      navigate("/thank-you", {
        state: { pageType: "redressAccepted" },
      });
    } catch {
      setShowError(true);
    } finally {
      setShowLoadingPage(false);
    }
  };

  return (
    <div>
      <MultiStepFormLayout
        methods={methods}
        onSubmit={onSubmit}
        currentStep={currentStep}
        totalSteps={totalSteps}
        steps={steps}
        showError={showError}
        onCloseError={() => setShowError(false)}
        errorTitleKey="errors.genericErrorBanner.couldntSaveBankDetails"
        errorBodyKey="errors.genericErrorBanner.body"
        submitLabelKey="bankDetailsForm.submitBankDetails"
        continueLabelKey="bankDetailsForm.continueToBank"
        submitDisabled={!isAgreed || methods.formState.isSubmitting}
        handleContinue={handleContinue}
        handleBack={handleBack}
      />
      {showLoadingPage && <Loader title={t("bankDetailsLoading.title")} subtitle={t("bankDetailsLoading.subtitle")} />}
    </div>
  );
};
